import React from 'react';
import classnames from 'classnames';
import snakeCase from 'lodash/snakeCase';
import { useMediaQuery } from 'react-responsive';
import { DialogButton, Typography } from '@guildeducationinc/recess';
import { ComponentDataKey } from '@src/components/Pages/Home/Layout/config';
import ProviderCallout from '@src/components/StudentLoan/ProviderCallout/ProviderCallout';
import ReimbursementCallout from '@src/components/StudentLoan/ReimbursementCallout/ReimbursementCallout';
import { ModalOptions, useModal } from '@src/context/ModalContext';
import { useMemberPlanContext } from '@src/context/MemberPlanContext';
import { useEmployerContent } from '@src/context/EmployerContentContext';
import { MOBILE_QUERY } from '@src/constants';
import { clickable } from '@src/analytics';
import useMount from '@src/hooks/useMount';
import './StudentLoanModal.scss';

export const STUDENT_LOAN_MODAL_LOCATION_ID = '#student-loans';

const StudentLoanModal: React.FC = () => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { memberPlan } = useMemberPlanContext();
  const { setModalOptions } = useModal();
  const dataKey = ComponentDataKey.STUDENT_LOAN;
  const { [dataKey]: content } = useEmployerContent() ?? {};

  const hasOptimization = memberPlan?.hasStudentLoanOptimization;
  const hasReimbursement = memberPlan?.hasStudentLoanReimbursement;

  const ssoClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'student_loan_modal',
      text: snakeCase(content?.modal.button.text),
      type: 'link',
    });
  };

  const primaryButtonConfig: DialogButton = {
    href: content?.modal.button.href,
    newWindowIcon: true,
    newWindowNotification: true,
    onClick: ssoClickable,
    target: '_blank',
    text: content?.modal.button.text ?? 'Go to Candidly',
  };

  const studentLoanModalTitle = content?.modal.title ?? 'Crush your student loan debt';

  const studentLoanModalOptions: ModalOptions = {
    primaryButton: primaryButtonConfig,
    closable: true,
    describedBy: 'student-loan-modal-description',
    labelledBy: 'student-loan-modal-title',
    tag: 'student_loan_modal',
    title: isMobile ? studentLoanModalTitle : undefined,
    titleComponent: 'h2',
    titleSize: 'h2',
  };

  const studentLoanModalClassNames = classnames(
    isMobile ? ['padding--x_16'] : ['padding--top_56', 'padding--bottom_24', 'padding--x_56']
  );

  useMount(() => {
    setModalOptions(studentLoanModalOptions);
  });

  return (
    <>
      <div className={studentLoanModalClassNames}>
        {!isMobile && (
          <Typography id='student-loan-modal-title' variant='h2'>
            {studentLoanModalTitle}
          </Typography>
        )}
        <Typography Component='p' id='student-loan-modal-description' className='padding--top_8'>
          {hasOptimization && hasReimbursement
            ? content?.modal.description.reimbursement
            : content?.modal.description.optimization}
        </Typography>
        <ProviderCallout />
        {(memberPlan?.hasTuitionReimbursement || memberPlan?.hasCoveredExpenses) && (
          <ReimbursementCallout />
        )}
      </div>
    </>
  );
};

export default StudentLoanModal;
